import { useState, useCallback, useEffect } from 'react';
import { ProjectGroup } from '@/types/projectGroup';
import { useLocalStorageManager } from './useLocalStorageManager';
import { logger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';

const CUSTOM_GROUPS_KEY = 'custom-groups';

export interface CustomGroup extends Omit<ProjectGroup, 'id'> {
  id: string;
  sort_order?: number;
}

export function useCustomGroups() {
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  const [customGroups, setCustomGroups] = useState<CustomGroup[]>([]);

  // Load custom groups from localStorage
  const loadGroups = useCallback(() => {
    try {
      const stored = getStandardValue<CustomGroup[]>(CUSTOM_GROUPS_KEY, []);
      setCustomGroups(stored);
      return stored;
    } catch (error) {
      logger.error('[CustomGroups] Error loading groups:', error);
      return [];
    }
  }, [getStandardValue]);

  // Save custom groups to localStorage
  const saveGroups = useCallback((groups: CustomGroup[]) => {
    try {
      setStandardValue(CUSTOM_GROUPS_KEY, groups);
      setCustomGroups(groups);
      return true;
    } catch (error) {
      logger.error('[CustomGroups] Error saving groups:', error);
      return false;
    }
  }, [setStandardValue]);

  // Add a new custom group
  const addGroup = useCallback((group: Omit<CustomGroup, 'id'>) => {
    const newGroup: CustomGroup = {
      ...group,
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      sort_order: group.sort_order || 0
    };

    const currentGroups = getStandardValue<CustomGroup[]>(CUSTOM_GROUPS_KEY, []);
    const updatedGroups = [...currentGroups, newGroup];
    
    if (saveGroups(updatedGroups)) {
      logger.info('[CustomGroups] Added new group:', newGroup.name);
      return newGroup;
    }
    return null;
  }, [getStandardValue, saveGroups]);

  // Update an existing custom group
  const updateGroup = useCallback((id: string, updates: Partial<Omit<CustomGroup, 'id'>>) => {
    const currentGroups = getStandardValue<CustomGroup[]>(CUSTOM_GROUPS_KEY, []);
    const groupIndex = currentGroups.findIndex(g => g.id === id);
    
    if (groupIndex === -1) {
      logger.warn('[CustomGroups] Group not found for update:', id);
      return false;
    }

    const updatedGroups = [...currentGroups];
    updatedGroups[groupIndex] = { ...updatedGroups[groupIndex], ...updates };
    
    if (saveGroups(updatedGroups)) {
      logger.info('[CustomGroups] Updated group:', id);
      return true;
    }
    return false;
  }, [getStandardValue, saveGroups]);

  // Delete a custom group
  const deleteGroup = useCallback((id: string) => {
    const currentGroups = getStandardValue<CustomGroup[]>(CUSTOM_GROUPS_KEY, []);
    const filteredGroups = currentGroups.filter(g => g.id !== id);
    
    if (filteredGroups.length === currentGroups.length) {
      logger.warn('[CustomGroups] Group not found for deletion:', id);
      return false;
    }

    if (saveGroups(filteredGroups)) {
      logger.info('[CustomGroups] Deleted group:', id);
      return true;
    }
    return false;
  }, [getStandardValue, saveGroups]);

  // Get a specific group by ID
  const getGroup = useCallback((id: string): CustomGroup | null => {
    const currentGroups = getStandardValue<CustomGroup[]>(CUSTOM_GROUPS_KEY, []);
    return currentGroups.find(g => g.id === id) || null;
  }, [getStandardValue]);

  // Check if a group name already exists
  const groupNameExists = useCallback((name: string, excludeId?: string): boolean => {
    const currentGroups = getStandardValue<CustomGroup[]>(CUSTOM_GROUPS_KEY, []);
    return currentGroups.some(g => g.name.toLowerCase() === name.toLowerCase() && g.id !== excludeId);
  }, [getStandardValue]);

  // Clear all custom groups
  const clearAllGroups = useCallback(() => {
    if (saveGroups([])) {
      logger.info('[CustomGroups] Cleared all groups');
      return true;
    }
    return false;
  }, [saveGroups]);

  // Import groups from database project_group field
  const importFromDatabase = useCallback(async () => {
    try {
      // Get unique project groups from database
      const { data: projects, error } = await supabase
        .from('projects')
        .select('project_group')
        .not('project_group', 'is', null)
        .not('project_group', 'eq', '')
        .limit(500);
      
      if (error) {
        logger.error('[CustomGroups] Error fetching project groups from database:', error);
        return false;
      }

      // Extract unique group names
      const uniqueGroupNames = [...new Set(
        projects
          .map(p => p.project_group?.trim())
          .filter(Boolean)
      )];

      logger.info('[CustomGroups] Found unique groups in database:', uniqueGroupNames);

      if (uniqueGroupNames.length === 0) {
        logger.info('[CustomGroups] No groups found in database to import');
        return true;
      }

      // Get current groups to avoid duplicates
      const currentGroups = getStandardValue<CustomGroup[]>(CUSTOM_GROUPS_KEY, []);
      const existingNames = currentGroups.map(g => g.name.toLowerCase());

      // Convert to CustomGroup format
      const newGroups: CustomGroup[] = uniqueGroupNames
        .filter(name => !existingNames.includes(name!.toLowerCase()))
        .map((name, index) => ({
          id: `db-imported-${Date.now()}-${index}`,
          name: name!,
          description: `Imported from database`,
          color: '#10b981',
          sort_order: currentGroups.length + index
        }));

      if (newGroups.length === 0) {
        logger.info('[CustomGroups] All database groups already exist in localStorage');
        return true;
      }

      // Merge with existing groups
      const mergedGroups = [...currentGroups, ...newGroups];
      
      if (saveGroups(mergedGroups)) {
        logger.info('[CustomGroups] Successfully imported groups from database:', newGroups.map(g => g.name));
        return true;
      }
      
      return false;
    } catch (error) {
      logger.error('[CustomGroups] Error importing from database:', error);
      return false;
    }
  }, [getStandardValue, saveGroups]);

  // Sync with database - run import automatically
  const syncWithDatabase = useCallback(async () => {
    await importFromDatabase();
  }, [importFromDatabase]);

  // Load groups on mount
  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  return {
    customGroups,
    addGroup,
    updateGroup,
    deleteGroup,
    getGroup,
    groupNameExists,
    clearAllGroups,
    loadGroups,
    saveGroups,
    importFromDatabase,
    syncWithDatabase
  };
}